import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { confirm } from '../../../shared/ui/confirmStore'
import {
  ORDER_INVOICE_STATUS_LABELS_RU,
  SELLER_ENTITY_LABELS_RU,
  VatMode,
  isOrderEditable,
} from '@crm/shared'
import type { SellerEntity } from '@crm/shared'

// Юрлицо-продавец в строке счёта: « · ООО «СПЕЦМАШЭЛЕКТРО»». У старых счетов
// entityId может быть null — тогда сегмент не показываем.
function sellerEntityLabel(entityId: string | null): string {
  const label = entityId ? SELLER_ENTITY_LABELS_RU[entityId as SellerEntity] : undefined
  return label ? ` · ${label}` : ''
}
import type { InvoiceTemplatePayload, OrderDetailDto, OrderInvoiceDto } from '@crm/shared'
import {
  getInvoiceTemplate,
  getCombinedInvoiceTemplate,
  updateInvoice,
  deleteInvoice,
} from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'
import { generateInvoiceUnf } from '../utils/generateInvoiceUnf'
import { InvoiceItemsModal } from './InvoiceItemsModal'
import { formatMoney, formatDate } from '../orderFormat'

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось сформировать сводный шаблон'
}

/**
 * Предупреждение перед выгрузкой счёта в УНФ (п. 15 встречи 17.09, 19.09.2026).
 * Если у позиции нет наименования, в шаблон уходит сам партномер — в счёте
 * заказчика получается строка без названия товара (случай заявки ПНППК
 * 27.08.26-1). Показываем список и даём решить: дописать наименования или
 * выгружать как есть.
 */
async function confirmMissingNames(payload: InvoiceTemplatePayload): Promise<boolean> {
  const missing = payload.rows.filter(
    (r) => !r.name?.trim() || r.name.trim().toUpperCase() === r.partNumber.trim().toUpperCase(),
  )
  if (missing.length === 0) return true
  const sample = missing
    .slice(0, 5)
    .map((r) => r.partNumber)
    .join(', ')
  return confirm({
    title: 'В счёте есть позиции без наименования',
    message:
      `Позиций без наименования: ${missing.length}. В шаблон уйдёт партномер вместо названия товара: ` +
      `${sample}${missing.length > 5 ? ' и другие' : ''}. ` +
      'Наименование можно вписать в позициях счёта. Выгрузить как есть?',
    confirmText: 'Выгрузить как есть',
  })
}

export function InvoicesSection({
  order,
  canMutate,
}: {
  order: OrderDetailDto
  canMutate: boolean
}) {
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null)
  // Берём открытый счёт из свежего order (после правки цены он перечитывается) —
  // иначе модал показывал бы устаревший снимок.
  const openInvoice = openInvoiceId
    ? (order.invoices.find((i) => i.id === openInvoiceId) ?? null)
    : null

  // Сводный шаблон — один Excel по всем активным счетам, чтобы создать в УНФ
  // ОДИН заказ покупателя (сценарий «два КП → два счёта»). Счета в CRM
  // остаются раздельными. Кнопка видна при 2+ действующих счетах.
  const activeInvoices = order.invoices.filter((i) => i.status !== 'CANCELLED').length
  const combined = useMutation({
    mutationFn: async () => {
      const payload = await getCombinedInvoiceTemplate(order.id)
      if (!(await confirmMissingNames(payload))) return
      await generateInvoiceUnf(payload)
    },
    onError: (e) => toast.error(extractError(e)),
  })

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Счета ({order.invoices.length})</h2>
        <div className="flex items-center gap-3">
          {activeInvoices >= 2 && (
            <button
              className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs hover:bg-gray-200"
              title="Один Excel по всем действующим счетам — для создания в УНФ одного заказа покупателя"
              onClick={() => combined.mutate()}
              disabled={combined.isPending}
            >
              {combined.isPending ? 'Формирую…' : 'Скачать сводный шаблон'}
            </button>
          )}
          {order.invoices.length > 0 && (
            <span className="text-sm text-gray-600">
              Итого по счетам: <b className="text-gray-900">{formatMoney(order.invoicedSum)}</b>
            </span>
          )}
        </div>
      </div>
      {order.invoices.length === 0 ? (
        <p className="text-sm text-gray-500">Счетов нет.</p>
      ) : (
        <div className="space-y-2">
          {order.invoices.map((inv) => (
            <InvoiceRow
              key={inv.id}
              orderId={order.id}
              invoice={inv}
              canMutate={canMutate}
              onOpen={() => setOpenInvoiceId(inv.id)}
            />
          ))}
        </div>
      )}
      {openInvoice && (
        <InvoiceItemsModal
          invoice={openInvoice}
          orderId={order.id}
          canMutate={canMutate}
          canEditMoney={canMutate && isOrderEditable(order.status)}
          onClose={() => setOpenInvoiceId(null)}
        />
      )}
    </section>
  )
}

function InvoiceRow({
  orderId,
  invoice,
  canMutate,
  onOpen,
}: {
  orderId: string
  invoice: OrderInvoiceDto
  canMutate: boolean
  onOpen: () => void
}) {
  const qc = useQueryClient()
  const [num, setNum] = useState(invoice.invoiceNumber ?? '')
  const [editing, setEditing] = useState(false)

  const download = useMutation({
    mutationFn: async () => {
      const payload = await getInvoiceTemplate(orderId, invoice.id)
      if (!(await confirmMissingNames(payload))) return
      await generateInvoiceUnf(payload)
    },
  })
  const saveNum = useMutation({
    mutationFn: () => updateInvoice(invoice.id, { invoiceNumber: num.trim() || null }),
    onSuccess: () => {
      setEditing(false)
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
    },
  })
  const remove = useMutation({
    mutationFn: () => deleteInvoice(invoice.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['orders', orderId] }),
  })

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-100 bg-gray-50 px-3 py-2">
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onOpen}>
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium hover:underline">
            {invoice.invoiceNumber ? `Счёт № ${invoice.invoiceNumber}` : 'Счёт (без номера УНФ)'}
          </span>
          <span className="rounded-lg bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">
            {ORDER_INVOICE_STATUS_LABELS_RU[invoice.status]}
          </span>
          {/* Счёт меняли после выгрузки: правкой строк в окне счёта или правкой
              заказа (с 29.09.2026) — шаблон для УНФ нужно скачать заново. */}
          {invoice.status !== 'CANCELLED' &&
            (invoice.amountSource === 'manual' || invoice.amountEditedAt) && (
              <span
                className="rounded-lg bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700"
                title={`Счёт менялся после выгрузки${
                  invoice.amountEditedAt ? ` (${formatDate(invoice.amountEditedAt)})` : ''
                } — скачайте шаблон заново и обновите счёт в УНФ`}
              >
                ✎ изменён
              </span>
            )}
        </div>
        <div className="mt-0.5 text-xs text-gray-500">
          {invoice.items.length} поз. · {formatMoney(invoice.totalSum)} · НДС {invoice.vatRate}%
          {invoice.vatMode === VatMode.ON_TOP ? ' сверху' : ''}
          {sellerEntityLabel(invoice.entityId)} · {formatDate(invoice.createdAt)}
        </div>
      </button>
      {editing && (
        <div className="flex w-full items-center gap-2">
          <input
            className="rounded border border-gray-300 px-2 py-1 text-xs"
            placeholder="Номер счёта из УНФ"
            value={num}
            onChange={(e) => setNum(e.target.value)}
          />
          <button
            className="rounded-lg bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700"
            onClick={() => saveNum.mutate()}
            disabled={saveNum.isPending}
          >
            Сохранить
          </button>
          <button className="text-xs text-gray-500" onClick={() => setEditing(false)}>
            Отмена
          </button>
        </div>
      )}
      <div className="flex items-center gap-1">
        {canMutate && !editing && (
          <button
            className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
            onClick={() => setEditing(true)}
          >
            № из УНФ
          </button>
        )}
        <button
          className="rounded-lg bg-gray-100 px-2 py-1 text-xs hover:bg-gray-200"
          onClick={() => download.mutate()}
          disabled={download.isPending}
        >
          {download.isPending ? 'Формирую…' : 'Скачать шаблон'}
        </button>
        {canMutate && (
          <button
            className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
            onClick={() => {
              void confirm({
                title: 'Удалить счёт?',
                message: 'Позиции снова станут доступны для нового счёта.',
                danger: true,
                confirmText: 'Удалить',
              }).then((ok) => ok && remove.mutate())
            }}
            disabled={remove.isPending}
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  )
}
